<template>
  <div class="pricing">
    <!-- 首屏：背景渐变、标题、三档价格卡和卖点卡 -->
    <div class="pricing-hero">
      <!-- 背景单独放一层绝对定位，免得容器被内容撑高时渐变跟着变长 -->
      <div class="hero-bg"></div>
      <div class="hero-main">
        <div class="hero-title">远程管理，如临本地</div>

        <div class="plan-list">
          <div class="plan-card" v-for="plan in planList" :key="plan.title">
            <!-- 热销推荐角标：贴卡片右上角，圆角靠卡片的 overflow:hidden 裁 -->
            <div class="plan-badge" v-if="plan.badge">{{ plan.badge }}</div>
            <div class="plan-title">{{ plan.title }}</div>
            <div class="plan-sub">{{ plan.sub }}</div>
            <div class="plan-price">
              <span class="price-symbol">¥</span>
              <span class="price-num">{{ plan.price }}</span>
              <span class="price-unit">/月</span>
            </div>
            <div class="plan-btn" :class="plan.btnClass" @click="onPlanBtnClick(plan)">
              {{ plan.btnText }}
            </div>
            <div class="plan-features">
              <div class="plan-feature" v-for="feature in plan.features" :key="feature">
                <!-- 勾选图标用 currentColor 上色，价格卡和对比表颜色不同 -->
                <svg class="feature-check" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M16.6667 5L7.80092 14.0396C7.63627 14.2074 7.3654 14.2059 7.20267 14.0361L3.33337 10"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span class="feature-text">{{ feature }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 2026-10-23 与控制台会员页同步：卖点卡模块整体注释，需要时恢复
        <div class="adv-list">
          <div class="adv-card" v-for="item in advList" :key="item.name">
            <div class="adv-icon-wrap" :style="{ background: item.iconBg }">
              <img class="adv-icon" :src="item.icon" alt="" />
            </div>
            <div class="adv-name">{{ item.name }}</div>
            <div class="adv-desc">{{ item.desc }}</div>
          </div>
        </div>
        -->
      </div>
    </div>

    <!-- 功能对比：主表 11 行 + 附加功能 4 行 -->
    <div class="compare">
      <div class="compare-title">功能对比</div>
      <div class="compare-head">
        <div class="col-label"></div>
        <div class="col-value" v-for="name in planNames" :key="name">{{ name }}</div>
      </div>
      <div
        class="compare-row"
        v-for="(row, rowIndex) in featureRows"
        :key="row.label"
        :class="{ 'compare-row-last': rowIndex === featureRows.length - 1 }"
      >
        <div class="col-label">
          <svg v-if="row.star" class="row-star" viewBox="0 0 16 16" fill="none">
            <path
              d="M8.6662 3L6.62853 7.1592L2 7.83037L5.35297 11.1084L4.55147 15.6667L8.6662 13.4731L12.7818 15.6667L11.9862 11.1084L15.3333 7.83037L10.7304 7.1592L8.6662 3Z"
              fill="currentColor"
            />
            <path
              d="M2.99983 0.625L2.2357 2.1847L0.5 2.43639L1.75736 3.66564L1.4568 5.375L2.99983 4.5524L4.54318 5.375L4.24482 3.66564L5.5 2.43639L3.77391 2.1847L2.99983 0.625Z"
              fill="currentColor"
            />
          </svg>
          <span class="row-label">{{ row.label }}</span>
          <!-- 问号提示已去掉，代码留在这里，文案在 featureRowData 的 tip 里
          <span class="row-tip" v-if="row.tip">
            <svg class="row-tip-icon" viewBox="0 0 16 16" fill="none">
              <path
                d="M8.00002 10.667L8.00002 8.00033M8.00002 5.33366L8.00002 5.32699M1.33335 8.00033C1.33335 4.31843 4.31812 1.33366 8.00002 1.33366C11.6819 1.33366 14.6667 4.31843 14.6667 8.00033C14.6667 11.6822 11.6819 14.667 8.00002 14.667C4.31812 14.667 1.33335 11.6822 1.33335 8.00033ZM7.83335 5.33366C7.83335 5.24161 7.90797 5.16699 8.00002 5.16699C8.09207 5.16699 8.16669 5.24161 8.16669 5.33366C8.16669 5.42571 8.09207 5.50033 8.00002 5.50033C7.90797 5.50033 7.83335 5.42571 7.83335 5.33366Z"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span class="row-tip-bubble">{{ row.tip }}</span>
          </span>
          -->
        </div>
        <div class="col-value" v-for="(cell, cellIndex) in row.cells" :key="cellIndex">
          <svg v-if="cell.value === true" class="cell-check" viewBox="0 0 20 20" fill="none">
            <path
              d="M16.6667 5L7.80092 14.0396C7.63627 14.2074 7.3654 14.2059 7.20267 14.0361L3.33337 10"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg v-else-if="cell.value === false" class="cell-x" viewBox="0 0 20 20" fill="none">
            <path d="M15 5.4248L5 15.4248" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M5 5.4248L15 15.4248" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span v-else class="cell-main">
            {{ cell.value }}
            <template v-if="row.proArrow && cellIndex === lastCellIndex">
              <svg class="cell-arrow" viewBox="0 0 16 16" fill="none">
                <path
                  d="M12 2.33301L5 4.99967L8.33333 6.66634C8.33333 6.66634 5.43 13.1463 1 13.333C1 13.333 7.52667 15.7797 11.6667 7.99967L15 9.66634L12 2.33301Z"
                  fill="url(#pricing_arrow_gradient)"
                />
                <defs>
                  <linearGradient id="pricing_arrow_gradient" x1="1" y1="8.01539" x2="15" y2="8.01539" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#3772FF" />
                    <stop offset="0.36" stop-color="#AA56FF" />
                    <stop offset="0.7" stop-color="#FF6838" />
                    <stop offset="1" stop-color="#FF9924" />
                  </linearGradient>
                </defs>
              </svg>
              <span class="cell-arrow-text">{{ row.proArrowText }}</span>
            </template>
            <br v-if="cell.sub" />
            <span class="cell-sub" v-if="cell.sub">{{ cell.sub }}</span>
          </span>
        </div>
      </div>

      <div class="compare-sub-title">附加功能</div>
      <div
        class="compare-row"
        v-for="(row, rowIndex) in extraRows"
        :key="row.label"
        :class="{ 'compare-row-last': rowIndex === extraRows.length - 1 }"
      >
        <div class="col-label">
          <svg v-if="row.star" class="row-star" viewBox="0 0 16 16" fill="none">
            <path
              d="M8.6662 3L6.62853 7.1592L2 7.83037L5.35297 11.1084L4.55147 15.6667L8.6662 13.4731L12.7818 15.6667L11.9862 11.1084L15.3333 7.83037L10.7304 7.1592L8.6662 3Z"
              fill="currentColor"
            />
            <path
              d="M2.99983 0.625L2.2357 2.1847L0.5 2.43639L1.75736 3.66564L1.4568 5.375L2.99983 4.5524L4.54318 5.375L4.24482 3.66564L5.5 2.43639L3.77391 2.1847L2.99983 0.625Z"
              fill="currentColor"
            />
          </svg>
          <span class="row-label">{{ row.label }}</span>
        </div>
        <div class="col-value" v-for="(cell, cellIndex) in row.cells" :key="cellIndex">
          <svg v-if="cell.value === true" class="cell-check" viewBox="0 0 20 20" fill="none">
            <path
              d="M16.6667 5L7.80092 14.0396C7.63627 14.2074 7.3654 14.2059 7.20267 14.0361L3.33337 10"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg v-else-if="cell.value === false" class="cell-x" viewBox="0 0 20 20" fill="none">
            <path d="M15 5.4248L5 15.4248" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M5 5.4248L15 15.4248" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span v-else class="cell-main">{{ cell.value }}</span>
        </div>
      </div>
    </div>

    <!-- 常见问题 -->
    <div class="faq">
      <div class="faq-title">常见问题</div>
      <div
        class="faq-item"
        v-for="(item, index) in faqList"
        :key="item.title"
        :class="{ 'faq-item-open': item.expanded }"
        @click="toggleFaq(index)"
      >
        <div class="faq-question">
          <span class="faq-question-text">{{ item.title }}</span>
          <svg class="faq-chevron" viewBox="0 0 24 24" fill="none">
            <path
              d="M8 10.5L12 14.5L16 10.5M22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12Z"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div class="faq-answer" :style="faqAnswerStyle(index)">
          <div class="faq-answer-inner" :ref="'faqAnswer' + index" v-html="item.answer"></div>
        </div>
      </div>
    </div>

    <Foot></Foot>
  </div>
</template>

<script>
  import Foot from '@/components/Foot.vue'
  import { mapActions } from 'vuex'

  // 控制台购买页，购买按钮跳这里下单（124 尊享版 / 125 专业版）
  const CONSOLE_ORDER_URL = 'https://yc.iepose.com/jdxb_console/vip/order'

  export default {
    name: 'Pricing',
    components: {
      Foot,
    },
    data: function () {
      return {
        // 对比表三列的列名，顺序与价格卡一致
        planNames: ['体验版', '尊享版', '专业版'],
        // 专业版在最后一列，用来判断专属标记
        lastCellIndex: 2,
        // 缓存答案高度，用于展开动画
        faqHeights: [],
        planList: [
          {
            title: '体验版',
            sub: '提供基础远程访问功能',
            price: '0',
            btnText: '立即下载',
            btnClass: 'plan-btn-download',
            action: 'download',
            features: ['20台绑定设备', '3台访问授权', '2-4Mbps中转带宽', '内网穿透1条2Mbps'],
          },
          {
            title: '尊享版',
            sub: '推荐个人/家庭使用',
            price: '8.3',
            btnText: '立即购买',
            btnClass: 'plan-btn-premium',
            action: 'buy',
            vipType: 124,
            features: [
              '40台绑定设备',
              '10台访问授权',
              '12Mbps中转带宽',
              '内网穿透1条2Mbps + 1条12Mbps',
              '1台网关授权',
            ],
          },
          {
            title: '专业版',
            sub: '适合团队和中小企业',
            price: '40.0',
            btnText: '立即购买',
            btnClass: 'plan-btn-pro',
            action: 'buy',
            vipType: 125,
            badge: '热销推荐',
            features: [
              '60台绑定设备',
              '20台访问授权',
              '15Mbps中转带宽',
              '内网穿透1条2Mbps + 2条12Mbps',
              '1台网关授权',
            ],
          },
        ],
        advList: [
          {
            icon: require('@/assets/pricing/shield-lock.svg'),
            iconBg: 'rgba(255, 153, 36, 0.15)',
            name: '加密传输',
            desc: '点对点直连不经过第三方服务器，数据只属于你。',
          },
          {
            icon: require('@/assets/pricing/archery-arrow.svg'),
            iconBg: 'rgba(0, 193, 134, 0.15)',
            name: '智能调度技术',
            desc: '自动探测最优线路，NAT直连极高成功率，秒切高速中继。',
          },
          {
            icon: require('@/assets/pricing/age-limit-3.svg'),
            iconBg: 'rgba(170, 86, 255, 0.15)',
            name: '3步轻松完成',
            desc: '安装，登陆，使用。没有复杂配置，无需公网IP。',
          },
        ],
        featureRowData: [
          {
            label: 'P2P通道',
            free: '不限速率，不限流量',
            premium: '不限速率，不限流量',
            pro: '不限速率，不限流量',
            tip: 'P2P通道为设备直连传输，不经中转服务器，不限速、不限流量；无法建立直连时自动切换转发通道。',
          },
          {
            label: '中转带宽',
            free: '2-4Mbps',
            premium: '12Mbps',
            pro: '15Mbps',
            proArrow: true,
            proArrowText: '提升25%',
          },
          {
            label: '访问授权',
            free: '3台',
            premium: '10台',
            pro: '20台',
            tip: '用于管理「客户端主动访问组网地址」的权限：Windows、macOS、Android、iOS 需授权，NAS、路由器、Linux 等设备端默认免授权。',
          },
          {
            label: '内网穿透',
            free: '1条2Mbps',
            freeSub: '1G流量',
            premium: '1条 2Mbps + 1条12Mbps',
            premiumSub: '不限流量',
            pro: '1条 2Mbps + 2条12Mbps',
            proSub: '不限流量',
            star: true,
          },
          { label: '网关授权', free: false, premium: '1台', pro: '1台' },
          {
            label: '二层组网',
            free: false,
            premium: false,
            pro: true,
            tip: '让异地设备像连接在同一个局域网中一样通信，支持局域网设备发现与依赖广播、组播的应用。',
          },
          { label: '广播 / 组播', free: false, premium: false, pro: true, star: true },
          {
            label: '自定义组网',
            free: false,
            premium: true,
            pro: true,
            tip: '以任意一台已安装节点小宝的设备作为中转，把它本机或同局域网内其他设备的 IP 发布到组网中，被访问设备无需安装客户端。',
          },
          { label: 'VIP群', free: false, premium: true, pro: true },
          { label: '绑定设备', free: '20台', premium: '40台', pro: '60台' },
        ],
        extraRowData: [
          { label: '远程桌面', free: true, premium: true, pro: true, star: true },
          { label: '相册备份', free: '单通道', premium: true, pro: true },
          { label: '远程文件', free: '单通道', premium: true, pro: true },
        ],
        faqList: [
          {
            title: '节点小宝是什么？核心能解决什么问题？',
            answer:
              '节点小宝是轻量化无需公网IP组网工具，p2p不限速不限流量、无需路由器设置、无需专业网络知识、无需搭建服务器，一站式实现内网外网互通、跨地域设备互联。远程办公、NAS访问、设备运维、异地联机、门店组网、工控调试等场景。',
            expanded: false,
          },
          {
            title: '内网穿透安全吗，外网其他人能随意访问吗？',
            answer:
              '内网穿透不限流量，全程加密传输，自带访问校验；支持访问区域、访问密码、来源限定三重防护，未授权用户无法接入，可杜绝外网非法访问。',
            expanded: false,
          },
          {
            title: '小宝网关作用是什么？和普通组网最大区别？',
            answer:
              '普通组网：每一台设备都要安装节点小宝客户端<br>小宝网关：访问端不用装客户端。例如路由器开启网关，全屋摄像头、打印机、电视、工控机、老旧设备无需安装客户端，实现整网免装软件访问组网。适用企业办公、连锁店等。',
            expanded: false,
          },
          {
            title: '小宝网关和自定义组网的区别是什么？',
            answer: '小宝网关是解决访问、自定义组网是解决被访问<br>访问和被访问的区别',
            expanded: false,
          },
          {
            title: '组网数据会泄露吗？平台会抓取数据吗？',
            answer:
              'P2P直连数据点对点传输，不经过平台服务器；中转通道采用AES全域加密，平台不存储、不抓取用户访问数据，组网数据用户自主可控。',
            expanded: false,
          },
          {
            title: '如何申请开具发票？',
            answer: '请在控制台订单记录中申请开具发票<br>将在15个工作日内开具电子发票',
            expanded: false,
          },
        ],
      }
    },
    computed: {
      // 把 free/premium/pro 摊平成数组，模板一个 v-for 渲染三列，省得写三份
      featureRows: function () {
        return this.buildRows(this.featureRowData)
      },
      extraRows: function () {
        return this.buildRows(this.extraRowData)
      },
    },
    methods: {
      ...mapActions(['sendLog']),
      // 整理成「标签 + 三列取值」
      buildRows: function (rowData) {
        return rowData.map(function (row) {
          return {
            label: row.label,
            star: row.star,
            tip: row.tip,
            proArrow: row.proArrow,
            proArrowText: row.proArrowText,
            cells: [
              { value: row.free, sub: row.freeSub },
              { value: row.premium, sub: row.premiumSub },
              { value: row.pro, sub: row.proSub },
            ],
          }
        })
      },
      // 体验版跳下载页，尊享版/专业版跳控制台购买页
      onPlanBtnClick: function (plan) {
        this.sendLog(['PC_pricing_clicked', plan.title + plan.btnText])
        if (plan.action === 'download') {
          this.$router.push('/download')
          return
        }
        this.openConsoleOrder(plan.vipType)
      },
      // 带上 from 渠道参数，跟导航「进入控制台」一致
      openConsoleOrder: function (vipType) {
        const url = CONSOLE_ORDER_URL + '?type=' + vipType
        window.open(this.$store.getters.rebuildUrl(url))
      },
      // 手风琴：展开时量出答案高度再过渡，收起归零
      toggleFaq: function (index) {
        const item = this.faqList[index]
        const willExpand = !item.expanded
        item.expanded = willExpand
        if (!willExpand) {
          return
        }
        const _that = this
        this.$nextTick(function () {
          _that.measureFaqAnswer(index)
        })
      },
      // 量出答案高度写进 faqHeights，供过渡使用
      measureFaqAnswer: function (index) {
        const answerRef = this.$refs['faqAnswer' + index]
        // ref 在 v-for 里拿到的是数组，取第一个
        const element = Array.isArray(answerRef) ? answerRef[0] : answerRef
        if (!element) {
          return
        }
        this.$set(this.faqHeights, index, element.scrollHeight)
      },
      // 高度和透明度用内联样式，才能走 transition
      faqAnswerStyle: function (index) {
        const height = this.faqHeights[index]
        return {
          maxHeight: this.faqList[index].expanded ? (height || 0) + 'px' : '0px',
          opacity: this.faqList[index].expanded ? 1 : 0,
        }
      },
    },
    mounted() {
      // 先占位，避免 $set 之前取到 undefined
      this.faqList.forEach(() => {
        this.faqHeights.push(0)
      })
      // 默认展开的条目要立刻量高度，否则显示不出来
      const _that = this
      this.$nextTick(function () {
        _that.faqList.forEach(function (item, index) {
          if (item.expanded) {
            _that.measureFaqAnswer(index)
          }
        })
      })
    },
  }
</script>

<style scoped lang="less">
  /* 内容区 1100 宽；项目配了 px2rem，这里按设计尺寸写 px 即可 */
  .pricing {
    width: 100%;
    background: #f7f9fe;
    text-align: center;
  }

  /* ---------- 首屏 ---------- */
  .pricing-hero {
    position: relative;
    padding-top: 65px;

    .hero-bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      // 背景下缘要慢慢淡出，所以高度多留一截，再用一层渐变盖住底部
      height: 1180px;
      background-image: linear-gradient(
          180deg,
          rgba(247, 249, 254, 0) 70.8%,
          rgba(247, 249, 254, 0.25) 72%,
          rgba(247, 249, 254, 0.38) 74.6%,
          rgba(247, 249, 254, 0.38) 76.3%,
          rgba(247, 249, 254, 0.62) 78.8%,
          rgba(247, 249, 254, 0.75) 83.1%,
          rgba(247, 249, 254, 0.88) 87.3%,
          #f7f9fe 100%
        ),
        linear-gradient(90deg, #eef8ff 0%, #f1f1ff 50.63%, #fff7f3 100%);
      z-index: 0;
    }

    .hero-main {
      position: relative;
      z-index: 1;
      margin: 0 auto;
      width: 1100px;
    }

    .hero-title {
      height: 72px;
      font-size: 48px;
      font-weight: 700;
      line-height: 72px;
      // 渐变文字：背景裁进文字里，文字本身透明
      background: linear-gradient(
        90deg,
        #3772ff 0%,
        #aa56ff 35%,
        #ff6838 70%,
        #ff9924 100%
      );
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
    }
  }

  /* ---------- 三档价格卡 ---------- */
  .plan-list {
    display: flex;
    justify-content: space-between;
    margin-top: 40px;
  }

  .plan-card {
    position: relative;
    width: 350px;
    height: 480px;
    background: #ffffff;
    border-radius: 20px;
    // 裁掉热销推荐角标超出卡片圆角的部分
    overflow: hidden;
  }

  .plan-badge {
    position: absolute;
    top: 0;
    right: 0;
    width: 90px;
    height: 32px;
    font-size: 14px;
    font-weight: 500;
    line-height: 32px;
    color: #ffffff;
    background: linear-gradient(90deg, #ff6838 0%, #ff9924 100%);
    border-radius: 0 0 0 20px;
  }

  .plan-title {
    margin-top: 31px;
    font-size: 24px;
    font-weight: 700;
    line-height: 36px;
    color: #1d1f24;
  }

  .plan-sub {
    margin-top: 6px;
    font-size: 16px;
    line-height: 24px;
    color: #777e90;
  }

  .plan-price {
    margin-top: 17px;
    height: 56px;
    // 价格数字用 Arial，字形高度最接近设计
    font-family: Arial, "Microsoft Yahei", "微软雅黑", sans-serif;
    // 行高跟数字同高，让 ¥、数字、/月 基线对齐
    line-height: 56px;
    color: #1d1f24;

    .price-symbol {
      font-size: 20px;
      font-weight: 500;
    }

    .price-num {
      margin: 0 4px 0 2px;
      font-size: 48px;
      font-weight: 700;
    }

    .price-unit {
      font-size: 18px;
      font-weight: 500;
    }
  }

  .plan-btn {
    width: 240px;
    height: 50px;
    margin: 20px auto 0;
    font-size: 18px;
    font-weight: 500;
    line-height: 50px;
    text-align: center;
    cursor: pointer;
    box-sizing: border-box;
    transition: background 0.2s, color 0.2s, opacity 0.2s;
    // 触屏设备点击时不要浏览器默认的灰色高亮
    -webkit-tap-highlight-color: transparent;
  }

  // 实底按钮悬浮轻微压暗，跟介绍页一致
  .plan-btn-premium:hover,
  .plan-btn-pro:hover {
    opacity: 0.9;
  }

  .plan-btn-download {
    color: #3772ff;
    background: #ffffff;
    border: 1px solid #3772ff;
    border-radius: 8px;
  }

  // 描边按钮悬浮填充主色、文字转白，白底压透明度看不出来
  .plan-btn-download:hover {
    background: #3772ff;
    color: #ffffff;
  }

  .plan-btn-premium {
    color: #ffffff;
    background: linear-gradient(90deg, #3772ff 0%, #aa56ff 100%);
    border-radius: 12px;
  }

  .plan-btn-pro {
    color: #ffffff;
    background: linear-gradient(90deg, #ff6838 0%, #ff9924 100%);
    border-radius: 12px;
  }

  .plan-features {
    margin-top: 27px;
    padding-left: 55px;
    text-align: left;
  }

  .plan-feature {
    display: flex;
    align-items: flex-start;
    height: 21px;
    // 两行间距 41px，行高 21px，所以下边距 20px
    margin-bottom: 20px;
  }

  .plan-feature:last-child {
    margin-bottom: 0;
  }

  .feature-check {
    flex: none;
    width: 20px;
    height: 20px;
    margin-right: 11px;
    color: #ff9924;
  }

  .feature-text {
    font-size: 14px;
    line-height: 21px;
    color: #57576c;
  }

  /* ---------- 三个卖点卡 ---------- */
  .adv-list {
    display: flex;
    justify-content: space-between;
    margin-top: 80px;
  }

  .adv-card {
    width: 350px;
    height: 200px;
    border-radius: 16px;
    // 上白下透明的竖向渐变
    background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
  }

  .adv-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin: 30px auto 0;
    border-radius: 12px;
  }

  .adv-icon {
    width: 27px;
    height: 27px;
  }

  .adv-name {
    margin-top: 19px;
    font-size: 20px;
    font-weight: 500;
    line-height: 29px;
    color: #1d1f24;
  }

  .adv-desc {
    margin-top: 7px;
    // 这个宽度能让三段文案按设计断行（「公网IP。」落到第二行）
    padding: 0 42.5px;
    // 拉丁字母用 Arial，雅黑的拉丁字形偏宽会提前折行
    font-family: Arial, "Microsoft Yahei", "微软雅黑", sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #57576c;
  }

  /* ---------- 功能对比表 ---------- */
  .compare {
    // 背景层是绝对定位，会盖住后面的静态内容，这里提一层
    position: relative;
    z-index: 1;
    margin: 80px auto 0;
    width: 1100px;
  }

  .compare-title {
    font-size: 40px;
    font-weight: 700;
    line-height: 56px;
    color: #1d1f24;
  }

  .compare-head {
    display: flex;
    // 表头文字贴顶，不居中
    align-items: flex-start;
    height: 56px;
    margin-top: 52px;
    font-size: 20px;
    font-weight: 500;
    color: #1d1f24;
  }

  // 表头借用了 .col-value 的列宽，字号和颜色要单独覆盖
  .compare-head .col-value {
    font-size: 20px;
    font-weight: 500;
    line-height: 29px;
    color: #1d1f24;
  }

  .compare-row {
    display: flex;
    align-items: center;
    height: 50px;
    // 行高含 1px 上边框，凑成 50px 行距
    box-sizing: border-box;
    // 设计稿：线是 #C8CFDE 加 40% 透明度
    border-top: 1px solid rgba(200, 207, 222, 0.4);
  }

  .compare-row-last {
    border-bottom: 1px solid rgba(200, 207, 222, 0.4);
  }

  .col-label {
    position: relative;
    width: 240px;
    padding-left: 78px;
    box-sizing: border-box;
    text-align: left;
  }

  .col-value {
    width: 290px;
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #57576c;
  }

  .col-value:last-child {
    width: 280px;
  }

  .row-star {
    position: absolute;
    top: 50%;
    left: 55px;
    width: 16px;
    height: 16px;
    margin-top: -8px;
    color: #ff9924;
  }

  .row-label {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #57576c;
  }

  .row-tip {
    position: relative;
    display: inline-block;
    margin-left: 2px;
    vertical-align: middle;
  }

  .row-tip-icon {
    display: block;
    width: 16px;
    height: 16px;
    color: #777e90;
  }

  .row-tip-bubble {
    position: absolute;
    top: 50%;
    left: 24px;
    width: 320px;
    padding: 12px 14px;
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    text-align: left;
    color: #ffffff;
    background: #1d1f24;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(23, 33, 94, 0.18);
    // 默认隐藏，鼠标悬浮到问号上才显示
    opacity: 0;
    visibility: hidden;
    transform: translateY(-50%);
    transition: opacity 0.2s;
    z-index: 5;
  }

  .row-tip:hover .row-tip-bubble {
    opacity: 1;
    visibility: visible;
  }

  .cell-check {
    width: 20px;
    height: 20px;
    color: #57576c;
    vertical-align: middle;
  }

  .cell-x {
    width: 20px;
    height: 20px;
    color: #57576c;
    vertical-align: middle;
  }

  .cell-main {
    color: #57576c;
  }

  .cell-sub {
    font-size: 12px;
    font-weight: 400;
    line-height: 17px;
  }

  .cell-arrow {
    width: 16px;
    height: 16px;
    margin: 0 4px 0 5px;
    vertical-align: -2px;
  }

  .cell-arrow-text {
    // 「提升25%」跟标题同一套四色渐变
    font-size: 12px;
    font-weight: 500;
    background: linear-gradient(
      90deg,
      #3772ff 0%,
      #aa56ff 36%,
      #ff6838 70%,
      #ff9924 100%
    );
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  .compare-sub-title {
    margin-top: 19px;
    // 与表格首行留 15px
    margin-bottom: 15px;
    padding-left: 78px;
    font-size: 18px;
    font-weight: 500;
    line-height: 27px;
    text-align: left;
    color: #1d1f24;
  }

  /* ---------- 常见问题 ---------- */
  .faq {
    // 同上：防止被首屏背景层盖住
    position: relative;
    z-index: 1;
    margin: 80px auto 0;
    padding-bottom: 104px;
    width: 1100px;
  }

  .faq-title {
    font-size: 40px;
    font-weight: 700;
    line-height: 60px;
    color: #1d1f24;
  }

  .faq-item {
    margin-top: 16px;
    background: #ffffff;
    border: 1px solid #e6e8ec;
    border-radius: 12px;
    cursor: pointer;
    // 关掉点击时的灰色高亮
    -webkit-tap-highlight-color: transparent;
  }

  /* 首个问题与标题的间距（用相邻选择器，避免 :first-of-type 被标题抢走） */
  .faq-title + .faq-item {
    margin-top: 50px;
  }

  .faq-question {
    display: flex;
    align-items: center;
    justify-content: space-between;
    // 收起时整条 64px（卡片上下各 1px 边框算在里面），用 min-height 固定，不受字体行高影响
    min-height: 62px;
    box-sizing: border-box;
    padding: 0 21px 0 30px;
  }

  .faq-question-text {
    font-size: 18px;
    font-weight: 500;
    line-height: 27px;
    color: #1d1f24;
  }

  .faq-chevron {
    flex: none;
    width: 24px;
    height: 24px;
    color: #b1b5c4;
    transition: transform 0.2s;
  }

  .faq-item-open .faq-chevron {
    transform: rotate(180deg);
  }

  .faq-answer {
    overflow: hidden;
    transition: max-height 0.3s ease, opacity 0.3s ease;
  }

  .faq-answer-inner {
    // 底部留白，让展开态整条高度跟设计一致
    padding: 0 30px 21px;
    font-size: 14px;
    line-height: 21px;
    text-align: left;
    color: #57576c;
  }
</style>
